import { Pipe, PipeTransform, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({
  name: 'safeHtml',
  standalone: false,
})
export class SafeHtmlPipe implements PipeTransform {
  constructor(private readonly _sanitizer: DomSanitizer) {}

  public transform(value: string, preserveStyles = false): SafeHtml {
    if (!value) {
      return this._sanitizer.bypassSecurityTrustHtml('');
    }

    const trimmed = value.trim();
    if (this._looksLikeSvg(trimmed)) {
      const safeSvg = this._sanitizeSvgStrict(trimmed);
      return this._sanitizer.bypassSecurityTrustHtml(safeSvg);
    }

    if (preserveStyles) {
      const safeHtmlWithStyles =
        this._sanitizeHtmlPreserveStylesRegexOnly(trimmed);
      return this._sanitizer.bypassSecurityTrustHtml(safeHtmlWithStyles);
    }

    // default html
    const safeHtml =
      this._sanitizer.sanitize(SecurityContext.HTML, trimmed) || '';
    return this._sanitizer.bypassSecurityTrustHtml(safeHtml);
  }

  private _looksLikeSvg(input: string): boolean {
    return (
      /^<\?xml[\s\S]*?\?>\s*<svg[\s>]/i.test(input) || /^<svg[\s>]/i.test(input)
    );
  }

  private _sanitizeSvgStrict(svgString: string): string {
    try {
      const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
      const root = doc.documentElement;
      if (root.tagName.toLowerCase() !== 'svg') {
        return '';
      }

      const forbidden = [
        'script',
        'foreignObject',
        'iframe',
        'object',
        'embed',
      ];
      forbidden.forEach((tag) =>
        doc.querySelectorAll(tag).forEach((n) => n.remove())
      );

      const allowedTags = new Set([
        'svg',
        'path',
        'g',
        'circle',
        'rect',
        'line',
        'polyline',
        'polygon',
      ]);
      const allowedAttrs = new Set([
        'viewBox',
        'xmlns',
        'fill',
        'stroke',
        'stroke-width',
        'stroke-linecap',
        'stroke-linejoin',
        'opacity',
        'd',
        'cx',
        'cy',
        'r',
        'x',
        'y',
        'x1',
        'y1',
        'x2',
        'y2',
        'points',
        'transform',
        'height',
        'width',
      ]);

      const cleanNode = (node: Element): Element | null => {
        const tag = node.tagName.toLowerCase();
        if (!allowedTags.has(tag)) {
          return null;
        }

        const el = document.createElementNS('http://www.w3.org/2000/svg', tag);

        for (const attr of Array.from(node.attributes)) {
          const lname = attr.name.toLowerCase();
          const val = attr.value;

          if (lname.startsWith('on')) {
            continue;
          }
          if (lname === 'style') {
            continue;
          }

          if (lname === 'href' || lname === 'xlink:href') {
            if (/^\s*(javascript:|data:)/i.test(val)) {
              continue;
            }
          }

          if (!allowedAttrs.has(attr.name) && !allowedAttrs.has(lname)) {
            continue;
          }
          el.setAttribute(attr.name, val);
        }

        for (const child of Array.from(node.children)) {
          const cleaned = cleanNode(child);
          if (cleaned) {
            el.appendChild(cleaned);
          }
        }

        return el;
      };

      const cleaned = cleanNode(root) as SVGSVGElement | null;
      if (!cleaned) {
        return '';
      }
      if (!cleaned.getAttribute('xmlns')) {
        cleaned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      }

      return new XMLSerializer().serializeToString(cleaned);
    } catch {
      return '';
    }
  }

  private _sanitizeHtmlPreserveStylesRegexOnly(html: string): string {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const forbiddenTags = new Set([
        'script',
        'iframe',
        'object',
        'embed',
        'form',
        'base',
        'applet',
        'meta',
        'link',
      ]);

      const cleanNode = (node: Node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as HTMLElement;
          const tag = el.tagName.toLowerCase();

          if (forbiddenTags.has(tag)) {
            el.remove();
            return;
          }

          // Remove dangerous attributes
          const attrs = Array.from(el.attributes);
          for (const attr of attrs) {
            const name = attr.name.toLowerCase();
            const val = attr.value.toLowerCase();

            // Block event handlers
            if (name.startsWith('on')) {
              el.removeAttribute(attr.name);
              continue;
            }

            // Block dangerous links
            if (
              (name === 'href' || name === 'src' || name === 'action') &&
              (val.includes('javascript:') || val.includes('data:'))
            ) {
              el.removeAttribute(attr.name);
              continue;
            }

            // Block dangerous CSS patterns in style attribute
            if (name === 'style' && this._isCssDangerous(val)) {
              el.removeAttribute(attr.name);
              continue;
            }
          }

          // Special handling for <style> tags content
          if (tag === 'style') {
            if (this._isCssDangerous(el.textContent || '')) {
              el.remove();
              return;
            }
          }
        }

        // Recurse children
        node.childNodes.forEach((child) => cleanNode(child));
      };

      doc.body.childNodes.forEach((node) => cleanNode(node));
      return doc.body.innerHTML;
    } catch (e) {
      console.error('PreserveStyle sanitization failed:', e);
      return this._sanitizer.sanitize(SecurityContext.HTML, html) || '';
    }
  }

  private _isCssDangerous(css: string): boolean {
    return /@import|expression\s*\(|url\s*\(\s*['"]?\s*(javascript:|data:)/i.test(
      css
    );
  }
}
